const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function parseInteger(value: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return parsed >= min && parsed <= max ? parsed : null;
}

function parseRelevantPositiveStep(value: string, maxRelevant: number): number | "larger" | null {
  if (!/^\d+$/.test(value)) return null;
  const normalized = value.replace(/^0+/, "");
  if (!normalized) return null;
  const maxText = String(maxRelevant);
  if (normalized.length > maxText.length) return "larger";
  const parsed = Number(normalized);
  return parsed > maxRelevant ? "larger" : parsed;
}

function formatTime(hour: number, minute: number): string {
  const suffix = hour >= 12 ? "pm" : "am";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${String(minute).padStart(2, "0")}${suffix}`;
}

function formatTimezone(timezone: string | null | undefined): string {
  return timezone?.trim() || "UTC";
}

function joinLabels(labels: string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

function formatLabeledToken(
  token: string,
  labels: string[],
  min: number,
  max: number,
  valueToIndex: (value: number) => number,
  unit: string,
): string | null {
  const wildcardStep = token.match(/^\*\/(\d+)$/);
  if (wildcardStep) {
    const interval = parseInteger(wildcardStep[1] ?? "", 1, max);
    return interval ? `every ${interval} ${unit}${interval === 1 ? "" : "s"}` : null;
  }

  const rangeStep = token.match(/^(\d+)-(\d+)\/(\d+)$/);
  if (rangeStep) {
    const start = parseInteger(rangeStep[1] ?? "", min, max);
    const end = parseInteger(rangeStep[2] ?? "", min, max);
    const interval = parseInteger(rangeStep[3] ?? "", 1, max);
    if (start === null || end === null || interval === null || start > end) return null;
    return `${labels[valueToIndex(start)]}-${labels[valueToIndex(end)]} every ${interval} ${unit}${interval === 1 ? "" : "s"}`;
  }

  const range = token.match(/^(\d+)-(\d+)$/);
  if (range) {
    const start = parseInteger(range[1] ?? "", min, max);
    const end = parseInteger(range[2] ?? "", min, max);
    if (start === null || end === null || start > end) return null;
    return `${labels[valueToIndex(start)]}-${labels[valueToIndex(end)]}`;
  }

  const value = parseInteger(token, min, max);
  return value === null ? null : labels[valueToIndex(value)]!;
}

function formatLabeledField(
  field: string,
  labels: string[],
  min: number,
  max: number,
  valueToIndex: (value: number) => number,
  unit: string,
): string | null {
  const parts = field.split(",");
  const formatted = parts.map((token) => formatLabeledToken(token, labels, min, max, valueToIndex, unit));
  return formatted.some((value) => value === null) ? null : joinLabels(formatted as string[]);
}

function formatWeekdayField(field: string): string | null {
  const labels = field.split(",").map((token) => {
    const wildcardStep = token.match(/^\*\/(\d+)$/);
    if (wildcardStep) {
      const interval = parseRelevantPositiveStep(wildcardStep[1] ?? "", 6);
      if (!interval) return null;
      if (interval === "larger") return [WEEKDAYS[0]!];
      return WEEKDAYS.filter((_, index) => index % interval === 0);
    }

    const rangeStep = token.match(/^(\d+)-(\d+)\/(\d+)$/);
    if (rangeStep) {
      const start = parseInteger(rangeStep[1] ?? "", 0, 6);
      const end = parseInteger(rangeStep[2] ?? "", 0, 6);
      const interval = parseRelevantPositiveStep(rangeStep[3] ?? "", 6);
      if (start === null || end === null || interval === null || start > end) return null;
      if (interval === "larger") return [WEEKDAYS[start]!];
      const values: string[] = [];
      for (let value = start; value <= end; value += interval) values.push(WEEKDAYS[value]!);
      return values;
    }

    const range = token.match(/^(\d+)-(\d+)$/);
    if (range) {
      const start = parseInteger(range[1] ?? "", 0, 6);
      const end = parseInteger(range[2] ?? "", 0, 6);
      if (start === null || end === null || start > end) return null;
      return [`${WEEKDAYS[start]}-${WEEKDAYS[end]}`];
    }

    const value = parseInteger(token, 0, 7);
    return value === null ? null : [WEEKDAYS[value % 7]!];
  });

  if (labels.some((value) => value === null)) return null;
  return joinLabels(labels.flat() as string[]);
}

function formatMonthField(field: string): string | null | undefined {
  if (field === "*") return undefined;
  return formatLabeledField(field, MONTHS, 1, 12, (value) => value - 1, "month");
}

function formatHourRange(field: string): string | null {
  if (!/^\d+-\d+$/.test(field)) return null;
  const [startRaw, endRaw] = field.split("-");
  const start = parseInteger(startRaw ?? "", 0, 23);
  const end = parseInteger(endRaw ?? "", 0, 23);
  if (start === null || end === null || start > end) return null;
  return `${formatTime(start, 0)} to ${formatTime(end, 59)}`;
}

export function humanizeCronSchedule(cronExpression: string | null | undefined, timezone?: string | null): string {
  if (!cronExpression?.trim()) return "no schedule";

  const fields = cronExpression.trim().split(/\s+/);
  if (fields.length !== 5) return "advanced schedule";

  const [minuteField, hourField, dayOfMonthField, monthField, dayOfWeekField] = fields;
  const minute = parseInteger(minuteField ?? "", 0, 59);
  const hour = parseInteger(hourField ?? "", 0, 23);
  const zone = formatTimezone(timezone);
  const month = formatMonthField(monthField ?? "");
  if (monthField !== "*" && !month) return "advanced schedule";

  if (minuteField?.startsWith("*/") && hourField === "*" && dayOfMonthField === "*" && monthField === "*" && dayOfWeekField === "*") {
    const interval = parseInteger(minuteField.slice(2), 1, 59);
    return interval ? `every ${interval} minutes` : "advanced schedule";
  }

  if (minuteField?.startsWith("*/") && dayOfMonthField === "*" && monthField === "*" && dayOfWeekField !== "*") {
    const interval = parseInteger(minuteField.slice(2), 1, 59);
    const hourRange = formatHourRange(hourField ?? "");
    const weekday = formatWeekdayField(dayOfWeekField ?? "");
    if (interval && hourRange && weekday) {
      return `every ${interval} minutes from ${hourRange} ${weekday} ${zone}`;
    }
  }

  if (minute !== null && hourField === "*" && dayOfMonthField === "*" && monthField === "*" && dayOfWeekField === "*") {
    return `hourly at :${String(minute).padStart(2, "0")} ${zone}`;
  }

  if (minute === null || hour === null) return "advanced schedule";

  const time = formatTime(hour, minute);
  const monthSuffix = month ? ` in ${month}` : "";

  if (dayOfMonthField === "*" && dayOfWeekField === "*") {
    if (month?.startsWith("every ")) return `${month} at ${time} ${zone}`;
    return `daily${monthSuffix} at ${time} ${zone}`;
  }

  if (dayOfMonthField === "*" && dayOfWeekField !== "*") {
    const weekday = formatWeekdayField(dayOfWeekField ?? "");
    if (!weekday) return "advanced schedule";
    return `weekly on ${weekday}${monthSuffix} at ${time} ${zone}`;
  }

  if (dayOfMonthField !== "*" && dayOfWeekField === "*") {
    const dayOfMonth = parseInteger(dayOfMonthField ?? "", 1, 31);
    if (dayOfMonth === null) return "advanced schedule";
    return `monthly${monthSuffix} on day ${dayOfMonth} at ${time} ${zone}`;
  }

  return "advanced schedule";
}
